import { useState, useRef, useCallback, useMemo } from 'react';
import { EditModeConfig, EditModeHookOptions, EditModeReturn } from '../types/editMode';

/**
 * Reusable Edit Mode Hook
 * 
 * Provides all edit mode functionality (CRUD operations, selection, validation)
 * Can be used with any entity type that has an 'id' field
 * 
 * @template T - Entity type (must have 'id: number')
 * @param config - Configuration object with API methods, field definitions, etc.
 * @param options - Options including data, callbacks
 * @returns All edit mode state and handlers
 */
export function useEditMode<T extends { id: number }>(
  config: EditModeConfig<T>,
  options: EditModeHookOptions<T>
): EditModeReturn<T> {
  const { data, onDataChange, onRefresh, onError } = options;
  const { apiMethods, fields, primaryKey = 'id' as keyof T, validate, prepareData } = config;

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectAllVersion, setSelectAllVersion] = useState(0);
  
  // Refs for performance (no re-renders)
  const editedRowsRef = useRef<Map<number, Partial<T>>>(new Map());
  const selectedRowsRef = useRef<Set<number>>(new Set());
  const originalRowsRef = useRef<Map<number, T>>(new Map());
  const tempIdCounterRef = useRef<number>(-1);
  
  // State for new rows - only updated when rows are added/removed, not on every edit
  const [newRows, setNewRows] = useState<T[]>([]);
  // Ref to track edits to new rows (like editedRowsRef for existing rows)
  const newRowsEditsRef = useRef<Map<number, Partial<T>>>(new Map());
  
  // Dialog refs
  const deleteDialogRef = useRef<{ open: () => void; close: () => void } | null>(null);
  const refreshNotificationRef = useRef<{ open: () => void; close: () => void } | null>(null);

  // Create a lookup map for O(1) access
  const dataMap = useMemo(() => {
    const map = new Map<number, T>();
    data.forEach(item => {
      const id = item[primaryKey] as number;
      if (id !== undefined && id !== null) {
        map.set(id, item);
      }
    });
    return map;
  }, [data, primaryKey]);

  // Display rows - include new rows at the top, then existing rows
  // Merge new rows with their edits from ref (no re-render on edit)
  const displayRows = useMemo(() => {
    const mergedNewRows = newRows.map(row => {
      const id = row[primaryKey] as number;
      const edits = newRowsEditsRef.current.get(id);
      if (edits) {
        return { ...row, ...edits } as T;
      }
      return row;
    });
    return [...mergedNewRows, ...data];
  }, [data, newRows, primaryKey]);

  // Handle cancel - revert all changes (defined early so handleEdit can use it)
  const handleCancel = useCallback(() => {
    // Revert all changes to original values
    if (originalRowsRef.current.size > 0) {
      const revertedData = data.map(item => {
        const id = item[primaryKey] as number;
        const original = originalRowsRef.current.get(id);
        return original ? { ...original } : item;
      });
      onDataChange(revertedData);
    }

    // Clear edited rows, new rows, and exit edit mode
    editedRowsRef.current.clear();
    selectedRowsRef.current.clear();
    newRowsEditsRef.current.clear();
    setNewRows([]);
    originalRowsRef.current.clear();
    setIsEditMode(false);
  }, [data, primaryKey, onDataChange]);

  // Enter/Exit edit mode
  const handleEdit = useCallback(() => {
    if (isEditMode) {
      // Exiting edit mode - discard changes
      handleCancel();
    } else {
      // Entering edit mode - store original values
      const originals = new Map<number, T>();
      data.forEach(item => {
        const id = item[primaryKey] as number;
        if (id !== undefined && id !== null) {
          originals.set(id, { ...item });
        }
      });
      originalRowsRef.current = originals;
      editedRowsRef.current.clear();
      selectedRowsRef.current.clear();
      setNewRows([]);
      tempIdCounterRef.current = -1;
      setIsEditMode(true);
    }
  }, [isEditMode, data, primaryKey, handleCancel]);

  // Handle cell edit - track changes in ref (no re-renders)
  const handleCellEdit = useCallback((rowId: number, columnId: string, value: any) => {
    // Check if this is a new row (negative ID)
    if (rowId < 0) {
      // Update new row edits in ref (no re-render) - same approach as existing rows
      const existingEdits = newRowsEditsRef.current.get(rowId) || {};
      newRowsEditsRef.current.set(rowId, { ...existingEdits, [columnId]: value });
      return;
    }

    // Get original row value - check originalRowsRef first (snapshot when edit mode started)
    // then fall back to dataMap (current data)
    const originalRow = originalRowsRef.current.get(rowId) || dataMap.get(rowId);
    if (!originalRow) return;
    
    const originalValue = originalRow[columnId as keyof T];
    
    // Use strict equality to handle 0 correctly
    // Special handling: if original had a value (not null/undefined) and new value is null,
    // this is a change (user is clearing the field) - always track it
    const isClearingField = (originalValue !== null && originalValue !== undefined) && value === null;
    
    // If user is clearing a field, always track the change
    if (isClearingField) {
      const existingEdits = editedRowsRef.current.get(rowId) || {};
      editedRowsRef.current.set(rowId, { ...existingEdits, [columnId]: value });
      return;
    }
    
    // For other cases, check if values are equal
    const valuesEqual = originalValue === value || 
      (originalValue === null && value === null) ||
      (originalValue === undefined && value === undefined) ||
      (originalValue === null && value === undefined) ||
      (originalValue === undefined && value === null);
    
    if (valuesEqual) {
      // Value matches original - remove from edits if it was edited
      const existingEdits = editedRowsRef.current.get(rowId);
      if (existingEdits && columnId in existingEdits) {
        const newEdits = { ...existingEdits };
        delete newEdits[columnId as keyof T];
        
        if (Object.keys(newEdits).length === 0) {
          editedRowsRef.current.delete(rowId);
        } else {
          editedRowsRef.current.set(rowId, newEdits);
        }
      }
      return;
    }

    // Track the edit in ref (doesn't trigger re-render)
    const existingEdits = editedRowsRef.current.get(rowId) || {};
    editedRowsRef.current.set(rowId, { ...existingEdits, [columnId]: value });
  }, [dataMap]);

  // Handle save - batch API calls
  const handleSave = useCallback(async () => {
    const editedEntries = Array.from(editedRowsRef.current.entries());
    const hasNewRows = newRows.length > 0;
    
    if (editedEntries.length === 0 && !hasNewRows) {
      // No changes to save - just exit edit mode
      setIsEditMode(false);
      originalRowsRef.current.clear();
      editedRowsRef.current.clear();
      setNewRows([]);
      return;
    }

    // Set saving state IMMEDIATELY (synchronously)
    setSaving(true);
    
    // Use setTimeout to ensure state update is processed
    await new Promise(resolve => setTimeout(resolve, 0));

    try {
      // First, save new rows
      if (hasNewRows) {
        for (const newRow of newRows) {
          // Merge with edits from ref
          const id = newRow[primaryKey] as number;
          const edits = newRowsEditsRef.current.get(id);
          const rowToSave = edits ? { ...newRow, ...edits } as T : newRow;
          
          // Validate if validation function provided
          if (validate) {
            const validationError = validate(rowToSave, true);
            if (validationError) {
              throw new Error(validationError);
            }
          }

          // Prepare data if prepareData function provided
          const dataToSave = prepareData ? prepareData(rowToSave) : rowToSave;

          await apiMethods.create(dataToSave);
        }
      }

      // Then, save edited rows in batches
      if (editedEntries.length > 0) {
        const batchSize = 10;
        const batches = [];
        for (let i = 0; i < editedEntries.length; i += batchSize) {
          batches.push(editedEntries.slice(i, i + batchSize));
        }

        // Process batches sequentially with small delays
        for (const batch of batches) {
          const batchPromises = batch.map(async ([rowId, changes]) => {
            const originalRow = originalRowsRef.current.get(rowId) || dataMap.get(rowId);
            if (!originalRow) return;

            // OPTIMIZATION: Only send changed fields, not all fields
            // This dramatically reduces payload size and processing time
            const updateData: Partial<T> = {};
            
            // Only include fields that actually changed
            Object.keys(changes).forEach(key => {
              const fieldKey = key as keyof T;
              updateData[fieldKey] = changes[fieldKey];
            });

            // Prepare data if prepareData function provided
            // Pass only changed fields (updateData) and originalRow for reference
            const dataToSave = prepareData ? prepareData(updateData, originalRow) : updateData;

            await apiMethods.update(rowId, dataToSave);
          });

          await Promise.all(batchPromises);
          
          // Small delay between batches
          if (batches.indexOf(batch) < batches.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 50));
          }
        }
      }
      
      // OPTIMIZATION: Update only changed rows instead of reloading all data
      // This is much faster for large datasets
      const updatedData = [...data];
      let hasUpdates = false;
      
      // Update edited rows in local data
      editedEntries.forEach(([rowId, changes]) => {
        const index = updatedData.findIndex(item => (item[primaryKey] as number) === rowId);
        if (index !== -1) {
          updatedData[index] = { ...updatedData[index], ...changes };
          hasUpdates = true;
        }
      });
      
      // If we have new rows, refresh to get their IDs from server
      // Otherwise, just update local data
      if (hasNewRows) {
        await onRefresh();
      } else if (hasUpdates) {
        onDataChange(updatedData);
      }
      
      // Clear edited rows, new rows, and exit edit mode
      editedRowsRef.current.clear();
      selectedRowsRef.current.clear();
      newRowsEditsRef.current.clear();
      setNewRows([]);
      originalRowsRef.current.clear();
      setIsEditMode(false);
    } catch (error) {
      console.error('Error saving:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to save changes. Please try again.';
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setSaving(false);
    }
  }, [newRows, apiMethods, dataMap, onRefresh, onError, validate, prepareData, data, onDataChange, primaryKey]);

  // Handle row selection - update ref and trigger re-render
  const handleRowSelect = useCallback((rowId: number, selected: boolean) => {
    if (selected) {
      selectedRowsRef.current.add(rowId);
    } else {
      selectedRowsRef.current.delete(rowId);
    }
    // Force minimal re-render so checkboxes and selection counts sync with ref
    setSelectAllVersion(prev => prev + 1);
  }, []);

  // Handle select all
  const handleSelectAll = useCallback((selected: boolean) => {
    if (selected) {
      // Select all visible rows
      const allRowIds = displayRows
        .filter(row => {
          const id = row[primaryKey] as number;
          return id !== undefined && id !== null;
        })
        .map(row => row[primaryKey] as number);
      selectedRowsRef.current = new Set(allRowIds);
    } else {
      // Deselect all
      selectedRowsRef.current.clear();
    }
    // Force minimal re-render so checkboxes sync with ref
    setSelectAllVersion(prev => prev + 1);
  }, [displayRows, primaryKey]);

  // Handle add new row
  const handleAddNew = useCallback(() => {
    const tempId = tempIdCounterRef.current;
    tempIdCounterRef.current = tempId - 1;
    
    // Create new row with default values from field config
    const newRow = {} as T;
    Object.keys(fields).forEach(key => {
      const fieldKey = key as keyof T;
      const fieldConfig = fields[fieldKey];
      if (fieldConfig.defaultValue !== undefined) {
        newRow[fieldKey] = fieldConfig.defaultValue;
      }
    });
    
    // Set the primary key to temporary negative ID
    (newRow as any)[primaryKey] = tempId;
    
    setNewRows(prev => [newRow, ...prev]);
  }, [fields, primaryKey]);

  // Handle delete click - open dialog
  const handleDeleteClick = useCallback(() => {
    const selectedIds = Array.from(selectedRowsRef.current);
    if (selectedIds.length > 0 && deleteDialogRef.current) {
      deleteDialogRef.current.open();
    }
  }, []);

  // Handle confirmed delete
  const handleConfirmDelete = useCallback(() => {
    const selectedIds = Array.from(selectedRowsRef.current);
    if (selectedIds.length === 0) {
      deleteDialogRef.current?.close();
      return;
    }

    // Close dialog instantly via ref (NO state update, NO re-render)
    deleteDialogRef.current?.close();

    // Immediately remove from local state for instant user feedback
    const selectedSet = new Set(selectedIds);
    if (onDataChange && data) {
      const remainingData = data.filter(item => !selectedSet.has(item[primaryKey] as number));
      onDataChange(remainingData);
    }

    // Clear selection immediately (ref only, NO re-render)
    selectedRowsRef.current.clear();
    setSelectAllVersion(prev => prev + 1);

    // Delete from server in background
    Promise.all(
      selectedIds.map(id => apiMethods.delete(id))
    ).then(() => {
      // Automatically refresh from DB to sync up
      if (onRefresh) {
        onRefresh();
      }
    }).catch((error) => {
      console.error('Error deleting records:', error);
      if (onError) {
        onError('Failed to delete records. Please try again.');
        // Revert local state by refreshing if deletion failed
        if (onRefresh) {
          onRefresh();
        }
      }
    });
  }, [apiMethods, onError, onDataChange, data, primaryKey, onRefresh]);

  // Helper to check if row is selected
  const isRowSelected = useCallback((rowId: number) => {
    return selectedRowsRef.current.has(rowId);
  }, []);

  // Count selected rows
  // selectAllVersion is used to trigger re-computation when selection changes
  // We reference it in the computation to satisfy ESLint, even though we don't use its value
  const selectedRowsCount = useMemo(() => {
    // Reference selectAllVersion to trigger re-computation when it changes
    void selectAllVersion;
    return selectedRowsRef.current.size;
  }, [selectAllVersion]);

  return {
    // State
    isEditMode,
    saving,
    selectAllVersion,
    
    // Refs
    selectedRowsRef,
    editedRowsRef,
    
    // Handlers
    handleEdit,
    handleSave,
    handleCancel,
    handleCellEdit,
    handleRowSelect,
    handleSelectAll,
    handleAddNew,
    handleDeleteClick,
    handleConfirmDelete,
    
    // Computed
    displayRows,
    isRowSelected,
    selectedRowsCount,
    
    // Dialog refs
    deleteDialogRef,
    refreshNotificationRef,
    
    // New rows
    newRows,
  };
}

